import { cn } from '@/lib/utils';
import * as Sentry from '@sentry/react';
import { HelpCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';

type FeedbackButtonProps = {
  collapsed?: boolean;
  className?: string;
  label?: string;
};

export const FeedbackButton = ({ collapsed, className, label = 'Vantar þig aðstoð?' }: FeedbackButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Feedback integration is lazy-loaded, so it may not be available immediately.
    // Poll briefly until it's ready.
    let cleanup: (() => void) | undefined;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tryAttach = () => {
      if (cancelled) {
        return true;
      }

      const feedback = Sentry.getFeedback();
      if (feedback && ref.current) {
        cleanup = feedback.attachTo(ref.current);
        return true;
      }
      return false;
    };

    if (!tryAttach()) {
      intervalId = setInterval(() => {
        if (tryAttach()) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = undefined;
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
          }
        }
      }, 500);
      // Stop trying after 10 seconds
      timeoutId = setTimeout(() => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = undefined;
        }
      }, 10000);
    }

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cleanup?.();
    };
  }, []);

  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={collapsed ? label : undefined}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 font-medium text-sm transition-all duration-200',
        'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground',
        collapsed && 'justify-center px-0',
        className
      )}
    >
      <HelpCircle className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </button>
  );
};

type HelpAndFeedbackProps = {
  collapsed?: boolean;
  onNavigate?: () => void;
};

export const HelpAndFeedback = ({ collapsed }: HelpAndFeedbackProps) => {
  if (collapsed) {
    return <FeedbackButton collapsed />;
  }

  return <FeedbackButton />;
};
